<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import ResourceListContainer from '@/resources/ResourceListContainer.vue'
import SDIStrategies from '@/sdi-strategies/SDIStrategies.vue'
import SDIStrategiesLabel from '@/sdi-strategies/SDIStrategiesLabel.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { useVisibilityStore } from '@/visibility/visibility.store'
import BigGoal from '../BigGoal.vue'
import CollapsibleGoalSection from '../CollapsibleGoalSection.vue'
import GenerateAssessmentTile from '../assessments/GenerateAssessmentTile.vue'
import { catalogBlockedReason } from '../assessments/generation_eligibility'
import TranslationContainer from '../translation/TranslationContainer.vue'
import { useAnchorPageStore } from '../anchor_page.store'
import { useGoalResourcesStore } from '../goal_resources.store'
import { useGoalTranslationStore } from '../goal_translation.store'
import { useAnchorPageTracking } from '../useAnchorPageTracking'
import AnchorAccordionObjectives from './AnchorAccordionObjectives.vue'
import AnnualTargetText from './AnnualTargetText.vue'

// Generation runs against a saved anchor page, so here the tile is a tease that names the next step.
const SAVE_TO_GENERATE_TEXT = 'Save this goal to your library to generate an assessment'
const BIG_GOAL_EVENT_CONTEXT = { obj_level: 'Annual' }

const props = withDefaults(defineProps<{
  goalLabel: string
  isReadyToTranslate?: boolean
  /** A library copy's text is the teacher's own, so it is always translated as edited text. */
  isTranslatedAsEdited?: boolean
  /** A library copy shows its own assessments in place of the save-first tease. */
  isGenerateTeaseShown?: boolean
  /** Whether the annualAssessments slot has anything to show, which opens the resource strip. */
  hasAnnualAssessments?: boolean
}>(), {
  isReadyToTranslate: false,
  isTranslatedAsEdited: false,
  isGenerateTeaseShown: true,
  hasAnnualAssessments: false
})

defineSlots<{
  goalReview?(): unknown
  objectivesReview?(): unknown
  goalText?(props: { openTranslation: (language: string) => void, closeTranslation: () => void }): unknown
  annualAssessments?(): unknown
  accordionObjectives?(): unknown
}>()

const anchorPageStore = useAnchorPageStore()
const { content, customPrimaryGoalText, isFetching, hasPMAs, hasAccordionObjectives } = storeToRefs(anchorPageStore)
const { isPremium } = storeToRefs(useUserStore())
const { aiFeatureVisibility, generateAssessmentVisibility } = storeToRefs(useVisibilityStore())
const goalResourcesStore = useGoalResourcesStore()
const translationStore = useGoalTranslationStore()
const { trackQuarterlyObjectivesToggled, trackSdiStrategiesToggled } = useAnchorPageTracking(getAnalyticsTracker())

const contentUuid = computed(() => content.value?.uuid ?? '')

const isGoalTextEdited = computed(() => props.isTranslatedAsEdited || customPrimaryGoalText.value !== content.value?.goal_text)
// Translation stays open per access point page, in its language, so returning to a tab translates again.
const requestedTranslationLanguage = computed(() => translationStore.requestedLanguageFor(contentUuid.value))
const isTranslationOpen = computed({
  get: () => translationStore.isTranslationActiveFor(contentUuid.value),
  set: (isOpen: boolean) => {
    if (isOpen) translationStore.activateTranslationFor(contentUuid.value)
    else translationStore.deactivateTranslationFor(contentUuid.value)
  }
})

function openTranslation(language: string) {
  translationStore.requestTranslationFor(contentUuid.value, language)
}

function closeTranslation() {
  isTranslationOpen.value = false
}

const isFetchingAnnualResources = computed(() => goalResourcesStore.isFetchingResourcesById(contentUuid.value))
const isGenerateTeaseVisible = computed(
  () => props.isGenerateTeaseShown && !isFetching.value && Boolean(contentUuid.value) && generateAssessmentVisibility.value
)
const isAnnualResourcesSectionShown = computed(
  () => (!isFetching.value && hasPMAs.value) || isGenerateTeaseVisible.value || props.hasAnnualAssessments
)
const isSdiSectionShown = computed(() => isPremium.value && aiFeatureVisibility.value)
const generationBlockedReason = computed(() => catalogBlockedReason(content.value))
</script>

<template>
  <h2 v-if="!hasAccordionObjectives" class="mb-6 mt-9 text-display-sm font-medium leading-tight">Annual Target</h2>
  <BigGoal :is-accordion-layout="hasAccordionObjectives" :event-context="BIG_GOAL_EVENT_CONTEXT">
    <slot name="goalReview" />
    <slot name="goalText" :open-translation="openTranslation" :close-translation="closeTranslation">
      <AnnualTargetText
        :goal-label="goalLabel"
        :is-accordion-layout="hasAccordionObjectives"
        :is-translate-button-shown="aiFeatureVisibility"
        :translation-languages="translationStore.languages"
        @translate="openTranslation"
        @reset="closeTranslation"
      />
    </slot>

    <template #translation>
      <TranslationContainer
        v-if="isReadyToTranslate"
        v-model:open="isTranslationOpen"
        :goal-title="content?.title"
        :goal-text="customPrimaryGoalText ?? ''"
        :goal-uuid="contentUuid"
        :is-edited="isGoalTextEdited"
        :requested-language="requestedTranslationLanguage"
        :languages="translationStore.languages"
      />
    </template>

    <template #footer>
      <div v-if="isAnnualResourcesSectionShown" data-testid="annual-resources">
        <ResourceListContainer
          :resources="goalResourcesStore.resourcesById(contentUuid)"
          :is-fetching-resources="isFetchingAnnualResources"
          :selected-language="goalResourcesStore.selectedLanguageById(contentUuid)"
          :is-tiled="hasAccordionObjectives"
          :is-indented="hasAccordionObjectives"
          :heading-level="3"
        >
          <div v-if="isGenerateTeaseVisible" :role="isFetchingAnnualResources ? undefined : 'listitem'" :class="{ 'pl-4': !hasAccordionObjectives }">
            <GenerateAssessmentTile
              target-label="Annual Target"
              :blocked-reason="generationBlockedReason"
              :blocked-text="SAVE_TO_GENERATE_TEXT"
            />
          </div>
          <!-- Tiled resources share one row with the copy's tiles; cards fill their rows, so there the tiles start an indented row of their own. -->
          <div v-else-if="hasAnnualAssessments" :class="hasAccordionObjectives ? 'contents' : 'flex w-full flex-wrap pl-4'" data-testid="annual-assessments">
            <slot name="annualAssessments" />
          </div>
        </ResourceListContainer>
      </div>

      <CollapsibleGoalSection
        v-if="hasAccordionObjectives"
        is-initially-expanded
        is-indented
        :heading-level="2"
        data-testid="accordion-objectives-section"
        @toggle="trackQuarterlyObjectivesToggled"
      >
        <template #label>
          <span class="material-icons-round mr-2 align-text-bottom" aria-hidden="true">format_list_bulleted</span>Objectives
        </template>
        <slot name="objectivesReview" />
        <slot name="accordionObjectives">
          <AnchorAccordionObjectives />
        </slot>
      </CollapsibleGoalSection>

      <CollapsibleGoalSection
        v-if="isSdiSectionShown"
        is-initially-expanded
        :is-indented="hasAccordionObjectives"
        :heading-level="hasAccordionObjectives ? 2 : undefined"
        data-testid="sdi-section"
        @toggle="trackSdiStrategiesToggled"
      >
        <template #label><SDIStrategiesLabel /></template>
        <SDIStrategies
          :goal-text="content?.plain_goal_text ?? ''"
          :goal-grades="content?.grades ?? []"
          :is-indented="hasAccordionObjectives"
        />
      </CollapsibleGoalSection>
    </template>
  </BigGoal>
</template>
